import SummaryProductList from "components/OrderDetail/SummaryProducts/SummaryProductsList"
import OrderDetailHOC from "hoc/OrderDetailHOC"
import { OrderDetailType } from "types/OrderType"
import useTransitOrder from "overrides/theme/privilege/base/Pages/DetailOrderScreens/Transit/UseTransitOrder"
import TransitAction from "components/OrderDetail/TransitProductOrder/TransitAction"
import SkeletonShippinGroupDetail from "components/Commons/Skeleton/SkeletonShippinGroupDetail"
import TagPrintContent from "components/OrderDetail/TagPrint/TagPrintContent"
import { Suspense, useContext, useEffect, useReducer, useState, useRef } from "react"
import { AuthContext } from "context/context/AuthContext"
import { clients } from "webapp-clients/Clients"
import AlertStyled from "theme/common/Alert.styled"
import { IconAttachment, IconBackArow } from "theme/common/Icon.styled"
import { ErrorFieldForm } from "theme/common/Paragraph.styled"
import useHideActionButtons from "hooks/UseHideActionButtons"
import { IconCancel } from "theme/common/Icon.styled"
import { ButtonStyled } from "theme/common/Button.styled"
import ContentDetailOrderActionsStyled from "theme/common/ContentDetailOrderActions.styled"
import { EShippinggroupAction } from "enums/shippinggroupEmun"
import useCancelDecreaseOrder from "hooks/UseCancelDecreaseOrder"
import UseDeliveredRejectOrder from "hooks/UseDeliveredRejectOrder"
import UseCustomerRejectOrder from "hooks/UseCustomerRejectOrder"
import { WebAppClientsEnum } from "enums/CommonEnum"
import UseCancelOrder from "hooks/UseCancelOrder"
import { useGetCancelActionByClient } from "hooks/UseGetMultiClient"
import pickingReducer, { initialStatePicking } from "pages/DetailOrderScreens/Confirm/PickingReducer"
import ProductItemRow from "overrides/theme/privilege/base/Pages/DetailOrderScreens/Confirm/ProductItemRow"
import ProcessProductsList from "components/OrderDetail/ProcessProducts/ProcessProductsList"
import { IProcessItem } from "interfaces/IItemsOrder"
import { LinkIconStyled } from "theme/common/Link.styled"
import { useLocation } from "react-router-dom"

// TODO: multiclient
import useFormConfig from "hooks/Media/useFormConfig"
import useFormConfigSandbox from "overrides/theme/sandbox/base/Hooks/Media/useFormConfig"
import { useEnv } from "hooks"
import styled from "styled-components"
import Modal from "components/Commons/Modal/Modal"
import ScannerItems from "../../../components/ProductScanner/ScannerItems"

const ProductListGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
  margin: 1rem 0;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const CancelModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  text-align: center;
  background-color: ${({ theme }) => theme.palette.white};
  padding: 2rem;
  border-radius: 8px;
  h3 {
    margin: 0;
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.palette.grey80};
  }
  .actions {
    display: flex;
    gap: 1rem;
    margin-top: 0.5rem;
    button {
      flex: 1;
      justify-content: center;
    }
  }
`

const CancelAlertModalContent = styled(CancelModalContent)`
  border: 2px solid #d32f2f;
  h3 {
    color: #d32f2f;
  }
  p {
    color: #b71c1c;
    font-weight: 500;
  }
  .danger-btn {
    background-color: #d32f2f;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 0.7rem 1.2rem;
    flex: 1;
    font-size: 1rem;
    cursor: pointer;
    font-weight: 600;
    &:hover {
      background-color: #b71c1c;
    }
  }
  .cancel-btn {
    background-color: transparent;
    color: #555;
    border: 1px solid #ccc;
    border-radius: 6px;
    padding: 0.7rem 1.2rem;
    flex: 1;
    font-size: 1rem;
    cursor: pointer;
    &:hover {
      background-color: #f5f5f5;
    }
  }
`

const CancelOrderBtn = styled(ButtonStyled)`
  background-color: #333 !important;
  border-color: #333 !important;
  color: #fff !important;
  min-width: 240px;
  height: 48px;
  border-radius: 8px;
  font-weight: 600;
  &:hover {
    background-color: #222 !important;
    border-color: #222 !important;
  }
`

const DestructiveBtn = styled(ButtonStyled)`
  background-color: #d32f2f !important;
  border-color: #d32f2f !important;
  color: #fff !important;
  &:hover {
    background-color: #b71c1c !important;
    border-color: #b71c1c !important;
  }
`

const TransitScreen = ({
  shippinGroupDetail,
  shippingGroupId,
  setFinishedShippingGroupAction,
  finishedShippingGroupAction,
  order,
}: OrderDetailType) => {
  const {
    state: { user },
  } = useContext(AuthContext)
  const { hideTransitActions, updateVisibilityButtonActions } = useHideActionButtons()
  const [_client] = useEnv().multiclient.get_user_env()
  const { pathname } = useLocation()
  const isTransitToSource = pathname.includes("transit_to_source")

  // State for series input (only for transit_to_source)
  const [pickingTransition, dispachPickingTransition] = useReducer(pickingReducer, initialStatePicking)
  const [itemsAllProcessed, setItemsAllProcessed] = useState(false)

  useEffect(() => {
    updateVisibilityButtonActions(shippinGroupDetail?.source?.id, shippinGroupDetail?.target?.source?.id)
  }, [shippinGroupDetail, updateVisibilityButtonActions])

  const client = clients[_client as keyof typeof clients]
  const FilesList = client["FilesList" as keyof typeof client]
  const itemsOrder = shippinGroupDetail?.source.items
  const documents = shippinGroupDetail?.custom?.documents
  const elockerInfo = shippinGroupDetail?.custom?.infoElocker
  const NoExistTransit = client["NoExistTransit" as keyof typeof client]

  // TODO: multiclient
  const useFormConfigWrapper = user?.mainGroup.id === "sandbox" ? useFormConfigSandbox : useFormConfig
  const { errors, FileUpload, handleSubmit, register, setValue } = useFormConfigWrapper({})

  const { submitDeliveredOrder, submitDeliveredRejectOrder, loadingTransit } = useTransitOrder({
    shippingGroupId,
    setFinishedShippingGroupAction,
    order,
    pickingTransition,
    isTransitToSource,
  })

  // Derived pending items logic (used for transit_to_source partial validation)
  const processedQuantities = (pickingTransition.processItems as IProcessItem[]).reduce(
    (acc: Record<string, number>, item) => {
      acc[item.sku] = (acc[item.sku] || 0) + (item.quantity || 1)
      return acc
    },
    {},
  )

  const displayPendingItems =
    itemsOrder
      ?.map((item) => {
        const processed = processedQuantities[item.sku] || 0
        const pending = (item.quantity || 1) - processed
        return { ...item, quantity: pending }
      })
      .filter((item) => (item.quantity || 0) > 0) || []

  const itemsAllProcessedOverride = displayPendingItems.length === 0

  // Check if all items are processed (only for transit_to_source)
  useEffect(() => {
    if (!isTransitToSource) return

    if (pickingTransition.isEditing) {
      setItemsAllProcessed(false)
      return
    }

    if (itemsAllProcessedOverride) {
      setItemsAllProcessed(true)
    } else {
      setItemsAllProcessed(false)
    }
  }, [itemsAllProcessedOverride, pickingTransition.isEditing, isTransitToSource])

  const allProductsRejected =
    itemsOrder &&
    itemsOrder.length > 0 &&
    itemsAllProcessedOverride &&
    !pickingTransition.processItems.some((pItem: IProcessItem) => pItem.isConfirm === true)

  const atLeastOneConfirmed = pickingTransition.processItems.some((pItem: IProcessItem) => pItem.isConfirm === true)

  const { handleClickCancelDecreaseOrder, loadingCancelDecrase, canIuseCancelDecraese } = useCancelDecreaseOrder({
    order,
    shippingGroupId,
  })

  const { handleClickDeliveredRejectOrder, loadingDeliveredReject, canIuseDeliveredReject } = UseDeliveredRejectOrder({
    order,
    shippingGroupId,
  })

  const { handleClickCustomerRejectOrder, loadingCustomerReject, canIuseCustomerReject } = UseCustomerRejectOrder({
    order,
    shippingGroupId,
    setFinishedShippingGroupAction,
  })

  const handleClickCancelDecrease = () => {
    handleClickCancelDecreaseOrder(EShippinggroupAction.cancel_decrease)
  }

  const handleClickDeliveredReject = () => {
    handleClickDeliveredRejectOrder(EShippinggroupAction.delivered_reject)
  }

  const handleClickCustomerReject = () => {
    handleClickCustomerRejectOrder(EShippinggroupAction.customer_reject)
  }

  const [permissionOrder, actionOrder] = useGetCancelActionByClient()

  const handleClickCancel = () => {
    handleClickCancelOrder(actionOrder)
  }

  const { handleClickCancelOrder, loadingCancel, canIuseCancel } = UseCancelOrder({
    order,
    shippingGroupId,
    permissionOrder,
  })

  const modalCancelRef = useRef<any>(null)
  const modalCancelConfirmRef = useRef<any>(null)

  const handleClickCancelFlow = () => {
    modalCancelRef.current?.open()
  }
  const confirmCancel = () => {
    modalCancelRef.current?.close()
    // Abrir segundo diálogo de alerta roja
    modalCancelConfirmRef.current?.open()
  }
  const confirmCancelFinal = () => {
    modalCancelConfirmRef.current?.close()
    submitDeliveredRejectOrder()
  }

  // Handlers for series input (only for transit_to_source)
  const handleClickProcessProduct = (item: IProcessItem) => {
    if (!item) return

    if (pickingTransition.isEditing) {
      const nextItemIndex = itemsOrder?.findIndex(
        (item) => !pickingTransition.processItems.some(({ sku }) => sku === item.sku),
      )
      dispachPickingTransition({
        type: "EDIT_ITEM_SERIES",
        payload: {
          itemEdit: item,
          nextItemIndex: nextItemIndex || -1,
        },
      })
      return
    }
    dispachPickingTransition({ type: "ADD_PROCESS_ITEM", payload: item })
  }

  const handleClickBackToScan = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    setItemsAllProcessed(false)
    dispachPickingTransition({ type: "RESET_PICKING" })
  }

  const handleClickEdit = (sku: string) => {
    dispachPickingTransition({ type: "CHANGE_ITEM_ACTIVE_FOR_EDIT", payload: sku })
  }

  return (
    <>
      {NoExistTransit && (
        <Suspense fallback={null}>
          <NoExistTransit
            shippingGroupId={shippingGroupId}
            courierDeliveryInfo={shippinGroupDetail?.custom?.courierDeliveryInfo}
            shippinGroupDetail={shippinGroupDetail}
          />
        </Suspense>
      )}
      {itemsOrder ? (
        <>
          {canIuseCancel && _client === WebAppClientsEnum.sandbox && (
            <ContentDetailOrderActionsStyled style={{ maxWidth: "400px", margin: "0 auto" }}>
              <ButtonStyled
                variant="secondary"
                onClick={handleClickCancel}
                loadingButton={loadingCancel}
                disabled={loadingCancel}
              >
                <IconCancel />
                <span>Cancelar Orden</span>
              </ButtonStyled>
            </ContentDetailOrderActionsStyled>
          )}

          {/* Show series input for transit_to_source */}
          {isTransitToSource && !finishedShippingGroupAction ? (
            <>
              {itemsAllProcessed && (
                <LinkIconStyled to="#" className="padding-content" onClick={handleClickBackToScan}>
                  <IconBackArow />
                  <span>Regresar</span>
                </LinkIconStyled>
              )}

              {!itemsAllProcessed && itemsOrder && !hideTransitActions && (
                <div style={{ maxWidth: "400px", padding: "1rem", margin: "0 auto" }}>
                  <ScannerItems remainingItems={displayPendingItems} onClicProcessProduct={handleClickProcessProduct} />
                </div>
              )}

              <AlertStyled variant="default">
                <p>Para continuar, necesita escanear todos los productos</p>
              </AlertStyled>
              {!itemsAllProcessed && displayPendingItems && (
                <ProductListGrid>
                  {displayPendingItems.map((item, index) => (
                    <ProductItemRow
                      key={item.sku + index}
                      itemOrder={item}
                      onClicProcessProduct={handleClickProcessProduct}
                      allOrderItems={itemsOrder}
                      source={shippinGroupDetail?.source.id}
                      hideConfirmActions={hideTransitActions}
                      pickingTransition={pickingTransition}
                      dispachPickingTransition={dispachPickingTransition}
                    />
                  ))}
                </ProductListGrid>
              )}

              {pickingTransition.processItems.length > 0 && (
                <div style={{ marginTop: "1rem" }}>
                  <AlertStyled variant="info">Productos Procesados</AlertStyled>
                  <ProcessProductsList
                    processItems={pickingTransition.processItems}
                    onClickEditItem={handleClickEdit}
                  />
                </div>
              )}

              {canIuseCancel && !hideTransitActions && allProductsRejected && (
                <ContentDetailOrderActionsStyled style={{ marginTop: "2rem", maxWidth: "400px", margin: "0 auto" }}>
                  <CancelOrderBtn
                    variant="secondary"
                    onClick={handleClickCancelFlow}
                    loadingButton={loadingTransit}
                    disabled={loadingTransit}
                  >
                    <IconCancel />
                    <span>Cancelar Orden</span>
                  </CancelOrderBtn>
                </ContentDetailOrderActionsStyled>
              )}
            </>
          ) : (
            <SummaryProductList
              itemsOrder={isTransitToSource ? pickingTransition.processItems : itemsOrder}
              groupPicking={false}
              titleMain={finishedShippingGroupAction ? "Productos en tránsito" : undefined}
            />
          )}

          {!finishedShippingGroupAction && (
            <>
              <form onSubmit={handleSubmit(submitDeliveredOrder)}>
                {FileUpload && (
                  <Suspense fallback={null}>
                    <section className="padding-content ">
                      <AlertStyled variant="info" className="m-top-button1">
                        <IconAttachment />
                        <p>Adjuntar máximo 3 archivos</p>
                      </AlertStyled>
                      <FileUpload register={register} setValue={setValue} />
                    </section>
                  </Suspense>
                )}

                {errors.filesShippinGroup && <ErrorFieldForm>{errors.filesShippinGroup.message}</ErrorFieldForm>}

                {canIuseCancelDecraese && (
                  <ContentDetailOrderActionsStyled style={{ maxWidth: "400px", margin: "0 auto" }}>
                    <ButtonStyled
                      variant="secondary"
                      onClick={handleClickCancelDecrease}
                      loadingButton={loadingCancelDecrase}
                      disabled={loadingCancelDecrase}
                    >
                      <IconCancel />
                      <span>Cancelar Orden sin Modificar Inventario</span>
                    </ButtonStyled>
                  </ContentDetailOrderActionsStyled>
                )}

                {canIuseDeliveredReject && (
                  <ContentDetailOrderActionsStyled style={{ maxWidth: "400px", margin: "0 auto" }}>
                    <ButtonStyled
                      variant="secondary"
                      onClick={handleClickDeliveredReject}
                      loadingButton={loadingDeliveredReject}
                      disabled={loadingDeliveredReject}
                    >
                      <IconCancel />
                      <span>Cancelar orden por siniestro</span>
                    </ButtonStyled>
                  </ContentDetailOrderActionsStyled>
                )}

                {canIuseCustomerReject && (
                  <ContentDetailOrderActionsStyled style={{ maxWidth: "400px", margin: "0 auto" }}>
                    <ButtonStyled
                      variant="secondary"
                      onClick={handleClickCustomerReject}
                      loadingButton={loadingCustomerReject}
                      disabled={loadingCustomerReject}
                    >
                      <IconCancel />
                      <span>Rechazado por el cliente</span>
                    </ButtonStyled>
                  </ContentDetailOrderActionsStyled>
                )}

                {!hideTransitActions && (!isTransitToSource || (itemsAllProcessed && atLeastOneConfirmed)) && (
                  <div style={{ maxWidth: "400px", margin: "0 auto" }}>
                    <TransitAction loadingTransit={loadingTransit} />
                  </div>
                )}
              </form>
            </>
          )}
          {order && (
            <TagPrintContent
              codigoEntrega={(elockerInfo?.isElocker && elockerInfo?.codigoEntrega) || null}
              order={order}
              shippingGroupId={shippingGroupId}
            />
          )}
          {FilesList && (
            <Suspense fallback={null}>
              <FilesList documents={documents} channel={shippinGroupDetail?.channel} orderId={order?.id} />
            </Suspense>
          )}
        </>
      ) : (
        <SkeletonShippinGroupDetail />
      )}

      <Modal ref={modalCancelRef}>
        <CancelModalContent>
          <h3>¿Está seguro que desea cancelar la orden?</h3>
          <p>Esta acción no se puede deshacer. La orden será cancelada completamente.</p>
          <div className="actions">
            <ButtonStyled variant="secondary" onClick={() => modalCancelRef.current?.close()}>
              <span>Volver atrás</span>
            </ButtonStyled>
            <DestructiveBtn variant="primary" onClick={confirmCancel}>
              <span>Confirmar Cancelación</span>
            </DestructiveBtn>
          </div>
        </CancelModalContent>
      </Modal>

      <Modal ref={modalCancelConfirmRef}>
        <CancelAlertModalContent style={{ backgroundColor: "rgba(255, 235, 235, 0.98)" }}>
          <h3>🚨 Reconfirmación de cancelación</h3>
          <p>
            ¿Está completamente seguro? Esta acción es <strong>irreversible</strong> y la orden será cancelada
            definitivamente.
          </p>
          <div className="actions">
            <button className="cancel-btn" onClick={() => modalCancelConfirmRef.current?.close()}>
              Volver
            </button>
            <button className="danger-btn" onClick={confirmCancelFinal}>
              Sí, cancelar orden
            </button>
          </div>
        </CancelAlertModalContent>
      </Modal>
    </>
  )
}

export default OrderDetailHOC(TransitScreen)
